import { useEffect, useId, useRef, useState } from 'react'
import { accessShort } from './accessLevels'
import './AccessLevelLegend.css'

/**
 * Legenda nivelurilor de acces (19 octombrie 2026), in locul balonului negru
 * cu un singur paragraf: cate un rand pe nivel - numele ingrosat si CE FIRME
 * vede omul - si un rand la capat, ca ce poate face vine din rol.
 *
 * E un buton adevarat: se deschide la mouse, la Tab (focus) si la clic (clicul
 * il tine deschis), si se inchide cu Escape sau la clic in afara. Textul e
 * legat de buton prin `aria-describedby`, deci cititorul de ecran il spune si
 * cand legenda e inchisa.
 *
 * Folosit in fisa contului (Roles & access), in New user si in Authentication
 * ("Default access") - oriunde era `Info` cu `accessLevelsHelp`.
 */
export function AccessLevelLegend({
  levels,
  what = 'Access levels',
}: {
  levels: readonly { value: number; label: string; help?: string }[]
  what?: string
}) {
  const id = useId()
  const box = useRef<HTMLSpanElement>(null)
  const [hover, setHover] = useState(false)
  const [focus, setFocus] = useState(false)
  const [pinned, setPinned] = useState(false)
  const open = hover || focus || pinned

  useEffect(() => {
    if (!pinned) return
    const away = (event: MouseEvent) => {
      if (box.current && !box.current.contains(event.target as Node)) setPinned(false)
    }
    document.addEventListener('mousedown', away)
    return () => document.removeEventListener('mousedown', away)
  }, [pinned])

  const close = () => {
    setHover(false)
    setFocus(false)
    setPinned(false)
  }

  return (
    <span
      ref={box}
      className="od-legend"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onKeyDown={(event) => {
        if (event.key === 'Escape' && open) {
          event.stopPropagation()
          close()
        }
      }}
    >
      <button
        type="button"
        className="od-legend-i"
        aria-label={`Legend: ${what}, what each level means`}
        aria-expanded={open}
        aria-describedby={id}
        onFocus={() => setFocus(true)}
        onBlur={() => setFocus(false)}
        onClick={() => setPinned((value) => !value)}
      >
        <i className="fas fa-info-circle" aria-hidden="true"></i>
      </button>
      <span id={id} role="tooltip" className="od-legend-pop" hidden={!open} data-testid="access-level-legend">
        <span className="od-legend-list">
          {levels.map((level) => (
            <span className="od-legend-row" key={level.value}>
              <strong>{level.label}</strong> &mdash; {accessShort(level)}
            </span>
          ))}
        </span>
        <span className="od-legend-foot">What the person can do comes from the role.</span>
      </span>
    </span>
  )
}
